import React, { useState, useEffect } from 'react';
import { Plus, X, ExternalLink, Check, Clock, AlertCircle, Ban } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { supabase } from '../../lib/supabaseClient';

const PLATFORM_TYPES = [
  { value: 'LinkedIn', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/username' },
  { value: 'GitHub', label: 'GitHub', placeholder: 'https://github.com/username' },
  { value: 'Portfolio', label: 'Portfolio', placeholder: 'https://yourportfolio.com' },
  { value: 'Other', label: 'Other', placeholder: 'https://...' },
];

const LINK_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'rejected', label: 'Rejected' },
];

const SocialLinksManager = ({ candidateId, isAdmin = false, showAddForm = true }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Form state for adding new link
  const [newLink, setNewLink] = useState({
    type: 'LinkedIn',
    customType: '',
    url: '',
  });

  useEffect(() => {
    fetchLinks();
  }, [candidateId]);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('candidates')
        .select('social_links')
        .eq('id', candidateId)
        .single();

      if (error) throw error;

      const socialLinks = data?.social_links || [];
      // Normalize 'verified' to 'active' for backward compatibility
      const normalizedLinks = (Array.isArray(socialLinks) ? socialLinks : []).map(link => ({
        ...link,
        status: link.status === 'verified' ? 'active' : link.status
      }));
      setLinks(normalizedLinks);
    } catch (err) {
      console.error('Error fetching social links:', err);
      setMessage({ type: 'error', text: 'Failed to load social links' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddLink = async () => {
    if (!newLink.url.trim()) {
      setMessage({ type: 'error', text: 'URL is required' });
      return;
    }

    if (newLink.type === 'Other' && !newLink.customType.trim()) {
      setMessage({ type: 'error', text: 'Platform name is required for Other type' });
      return;
    }

    // Basic URL validation
    try {
      new URL(newLink.url);
    } catch (e) {
      setMessage({ type: 'error', text: 'Please enter a valid URL' });
      return;
    }

    const linkToAdd = {
      type: newLink.type,
      ...(newLink.type === 'Other' && { customType: newLink.customType }),
      url: newLink.url,
      status: 'pending', // All new links start as pending
      added_at: new Date().toISOString(),
    };

    const updatedLinks = [...links, linkToAdd];

    try {
      setSaving(true);
      const { error } = await supabase
        .from('candidates')
        .update({ social_links: updatedLinks })
        .eq('id', candidateId);

      if (error) throw error;

      setLinks(updatedLinks);
      setNewLink({ type: 'LinkedIn', customType: '', url: '' });
      setMessage({ type: 'success', text: 'Link added successfully. Pending admin verification.' });
    } catch (err) {
      console.error('Error adding link:', err);
      setMessage({ type: 'error', text: 'Failed to add link' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLink = async (index) => {
    const updatedLinks = links.filter((_, i) => i !== index);

    try {
      setSaving(true);
      const { error } = await supabase
        .from('candidates')
        .update({ social_links: updatedLinks })
        .eq('id', candidateId);

      if (error) throw error;

      setLinks(updatedLinks);
      setMessage({ type: 'success', text: 'Link removed successfully' });
    } catch (err) {
      console.error('Error removing link:', err);
      setMessage({ type: 'error', text: 'Failed to remove link' });
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (index, newStatus) => {
    if (!isAdmin) return;

    const updatedLinks = [...links];
    updatedLinks[index] = {
      ...updatedLinks[index],
      status: newStatus,
      ...(newStatus === 'active' ? { verified_at: new Date().toISOString() } : { verified_at: null }),
    };

    try {
      setSaving(true);
      const { error } = await supabase
        .from('candidates')
        .update({ social_links: updatedLinks })
        .eq('id', candidateId);

      if (error) throw error;

      setLinks(updatedLinks);
      setMessage({
        type: 'success',
        text: `Link status changed to ${newStatus}`
      });
    } catch (err) {
      console.error('Error updating link status:', err);
      setMessage({ type: 'error', text: 'Failed to update link status' });
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
      case 'verified': // backward compatibility
        return (
          <Badge className="bg-green-100 text-green-800">
            <Check className="w-3 h-3 mr-1" />
            Active
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-100 text-red-800">
            <Ban className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-yellow-600 border-yellow-600">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  const getDisplayName = (link) => {
    return link.type === 'Other' && link.customType ? link.customType : link.type;
  };

  if (loading) {
    return (
      <Card className="w-full max-w-full overflow-hidden">
        <CardContent className="py-6">
          <p className="text-center text-muted-foreground">Loading social links...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-full overflow-hidden">
      <CardHeader>
        <CardTitle>Social & Professional Links</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          {isAdmin
            ? 'Manage and verify candidate social links'
            : 'Add your professional profiles (pending admin verification before public display)'}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {message && (
          <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
            {message.type === 'error' ? (
              <AlertCircle className="h-4 w-4" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        {/* Existing Links */}
        {links.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Current Links</h4>
            {links
              .map((link, index) => ({ ...link, originalIndex: index }))
              .filter(link => isAdmin || link.status === 'active' || link.status === 'verified')
              .map((link) => (
              <div
                key={link.originalIndex}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 border rounded-lg w-full max-w-full overflow-hidden"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{getDisplayName(link)}</span>
                      {!isAdmin && getStatusBadge(link.status)}
                    </div>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline flex items-center gap-1 mt-1 break-all"
                    >
                      {link.url}
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isAdmin && (
                    <select
                      value={link.status === 'verified' ? 'active' : link.status}
                      onChange={(e) => handleStatusChange(link.originalIndex, e.target.value)}
                      disabled={saving}
                      className="h-9 px-3 py-1 text-sm border rounded-md bg-background"
                    >
                      {LINK_STATUSES.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveLink(link.originalIndex)}
                    disabled={saving}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add New Link Form */}
        {showAddForm && (
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium mb-3">Add New Link</h4>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="platform-type">Platform</Label>
                <select
                  id="platform-type"
                  value={newLink.type}
                  onChange={(e) => setNewLink({ ...newLink, type: e.target.value, customType: '' })}
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                >
                  {PLATFORM_TYPES.map((platform) => (
                    <option key={platform.value} value={platform.value}>
                      {platform.label}
                    </option>
                  ))}
                </select>
              </div>

              {newLink.type === 'Other' && (
                <div>
                  <Label htmlFor="custom-type">Platform Name</Label>
                  <Input
                    id="custom-type"
                    value={newLink.customType}
                    onChange={(e) => setNewLink({ ...newLink, customType: e.target.value })}
                    placeholder="e.g., Figma, Tableau, Behance"
                    className="mt-1"
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="url">URL</Label>
              <Input
                id="url"
                type="url"
                value={newLink.url}
                onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                placeholder={PLATFORM_TYPES.find((p) => p.value === newLink.type)?.placeholder || 'https://...'}
                className="mt-1"
              />
            </div>

            <Button onClick={handleAddLink} disabled={saving} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Add Link
            </Button>
          </div>
        </div>
        )}

        {!isAdmin && !showAddForm && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              New links require admin verification before appearing on your public profile.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default SocialLinksManager;
